import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FASTAPI_HOST } from '../settings/config';
import { scheduleCategoriesData, socialSkillsData } from '../settings/staticData';
import {
  getCategoryDisplayName as getLocalizedCategoryDisplayName,
  getScheduleCategoryDisplayName,
  getScheduleSkillDisplayName,
} from '../settings/getCategoryDisplayName';
import MicTranscribeButton from '../components/MicTranscribeButton';
import { showStyledAlert } from '../components/StyledAlert';
import SocialSkillPage from './SocialSkillPage';
import TextStoryPage from './TextStoryPage';

const languageKey = (language) => (
  language === 'French' ? 'fr'
    : language === 'Traditional Chinese' ? 'zh-TW'
      : language === 'Simplified Chinese' ? 'zh-CN' : 'en'
);

export default function SocialStoryPage({
  currentLang,
  t,
  name,
  gender,
  age,
  cognitiveLevel,
  visualStyle,
  interest,
  pages,
  roleStoryLanguage,
  voice,
  userPlan,
  selectGenderInEnglish,
  getCategoryDisplayName,
  mode = 'social_story',
}) {
  const { signOut } = useAuth();
  const inputRef = useRef(null);
  const contentType = mode === 'visual_schedule' ? 'visual_schedule' : 'social_story';
  const [skillCategory, setSkillCategory] = useState('');
  const [socialSkill, setSocialSkill] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [presetOpen, setPresetOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [storyTitle, setStoryTitle] = useState('');
  const [storyRawTitle, setStoryRawTitle] = useState('');
  const [storyPages, setStoryPages] = useState([]);
  const [generatedContentType, setGeneratedContentType] = useState('social_story');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);

  const activeCategoryData = contentType === 'visual_schedule'
    ? scheduleCategoriesData : socialSkillsData;
  const currentLangKey = languageKey(currentLang);

  useEffect(() => {
    if (!selectedPreset) return;
    const translated = selectedPreset[currentLangKey] || selectedPreset.en || '';
    setPrompt(translated);
    setSocialSkill(selectedPreset);
  }, [currentLangKey, selectedPreset]);

  useEffect(() => {
    const skills = activeCategoryData[skillCategory] || [];
    if (skills.length > 0) setSocialSkill((value) => skills.includes(value) ? value : skills[0]);
    else setSocialSkill('');
  }, [activeCategoryData, skillCategory]);

  const requireToken = () => {
    const token = Cookies.get('user_token') || '';
    if (!token) setTimeout(() => signOut?.(), 1500);
    return token;
  };

  const generateStory = async () => {
    const token = requireToken();
    if (!token) return;
    if (!String(name || '').trim()) {
      showStyledAlert(t?.sb_missing_name || "Please set up your main character's name in Creation Settings.", { severity: 'warning' });
      return;
    }
    if (!prompt.trim()) {
      showStyledAlert(t?.sb_missing_required_fields || 'Please describe what you would like to create.', { severity: 'warning' });
      return;
    }

    setGenerating(true);
    setMediaUrl('');
    setMedia([]);
    try {
      const inputData = {
        name,
        gender: selectGenderInEnglish(gender, currentLang),
        age,
        environment_name: '',
        cognitive_level: cognitiveLevel,
        visual_style: visualStyle,
        skill_category: skillCategory,
        skill_category_display: contentType === 'visual_schedule'
          ? getScheduleCategoryDisplayName(skillCategory)
          : getCategoryDisplayName(skillCategory),
        social_skill: prompt.trim(),
        interest,
        pages,
        content_type: contentType,
        story_type: contentType === 'visual_schedule' ? 2 : 1,
        manual_environment_input: false,
        free_style_social_skill: skillCategory === 'Free_Style',
        language: roleStoryLanguage,
      };
      const { data } = await axios.post(`${FASTAPI_HOST}/api/generate_story`, inputData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStoryPages(Array.isArray(data?.pages) ? data.pages : []);
      setStoryTitle(data?.title || '');
      setStoryRawTitle(data?.raw_title ?? data?.title ?? '');
      setGeneratedContentType(contentType);
      setPreviewOpen(true);
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Unknown error';
      showStyledAlert(
        error.response?.status === 401
          ? `${message} Please sign out and sign in again.`
          : `Error: ${message}`,
        { severity: error.response?.status === 401 ? 'warning' : 'error' },
      );
    } finally {
      setGenerating(false);
    }
  };

  const generateMedia = async (selectedVoice, imageStyle, language, editedPages, editedTitle) => {
    const token = requireToken();
    if (!token) return;
    setMediaLoading(true);
    setMediaUrl('');
    setMedia([]);
    try {
      const title = editedTitle !== undefined ? editedTitle : storyTitle;
      const pagesToSend = editedPages || storyPages;
      const inputData = {
        name,
        gender: selectGenderInEnglish(gender, currentLang),
        age,
        environment_name: '',
        cognitive_level: cognitiveLevel,
        visual_style: visualStyle,
        skill_category: skillCategory,
        skill_category_display: generatedContentType === 'visual_schedule'
          ? getScheduleCategoryDisplayName(skillCategory)
          : getCategoryDisplayName(skillCategory),
        social_skill: prompt,
        interest,
        pages: pagesToSend.length || pages,
        content_type: generatedContentType,
        story_type: generatedContentType === 'visual_schedule' ? 2 : 1,
        manual_environment_input: false,
        free_style_social_skill: skillCategory === 'Free_Style',
        language,
        voice: selectedVoice,
        image_style: imageStyle,
        story_title: title,
        raw_title: storyRawTitle,
      };
      const formData = new FormData();
      formData.append('input_data', JSON.stringify(inputData));
      formData.append('story_pages', JSON.stringify(pagesToSend));
      if (title) formData.append('story_title', title);
      const { data } = await axios.post(`${FASTAPI_HOST}/api/generate_media`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMediaUrl(data?.media_url || data?.image_url || data?.html_url || data?.static_url || '');
      setMedia(data?.media || []);
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.error || error.message || '';
      showStyledAlert(`${t?.sb_media_error || 'Failed to generate media.'}${detail ? `\n${detail}` : ''}`, { severity: 'error' });
    } finally {
      setMediaLoading(false);
    }
  };

  const previewProgress = mediaLoading ? (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <LinearProgress sx={{ flex: 1, mr: 2, minWidth: 120 }} />
      <Typography variant="body2" sx={{ minWidth: 100 }}>Generating media...</Typography>
    </Box>
  ) : null;

  return (
    <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto', pt: 9, px: 2, pb: 2, boxSizing: 'border-box' }}>
      <Box sx={{ p: { xs: 1, sm: 2 }, minHeight: { xs: '48vh', sm: '60vh' }, bgcolor: { xs: 'transparent', sm: '#fff' } }}>
        <Typography variant="subtitle1" sx={{ mt: 4, mb: 3, fontWeight: 600, fontSize: { xs: 22, sm: 26 }, textAlign: 'center' }}>
          {contentType === 'visual_schedule'
            ? (t?.sb_schedule_input_message || 'What routine would you like to turn into a visual schedule?')
            : (t?.sb_story_input_message || 'What story would you like to create?')}
        </Typography>
        <TextField
          fullWidth multiline rows={5} color="inherit" value={prompt} inputRef={inputRef}
          onChange={(event) => { setPrompt(event.target.value); setSocialSkill(event.target.value); setSelectedPreset(null); }}
          placeholder={contentType === 'visual_schedule'
            ? (t?.sb_visual_schedule_help || 'Describe a routine, for example: getting ready for school')
            : (t?.sb_manual_social_skill_help || 'Enter your own social skill...')}
        />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={<Checkbox checked={presetOpen} onChange={(event) => setPresetOpen(event.target.checked)} color="inherit" />}
              label={contentType === 'visual_schedule'
                ? (t?.sb_choose_schedule || 'Choose a schedule')
                : (t?.sb_manual_social_skill || 'Choose a social skill')}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: { sm: 'auto' } }}>
            <MicTranscribeButton currentLang={currentLang} followUiLanguage disabled={generating}
              onTranscript={(value) => setPrompt((previous) => `${previous.trim()} ${value}`.trim())}
              onError={(message) => showStyledAlert(message, { severity: 'error' })}
            />
            <IconButton onClick={generateStory} disabled={generating} aria-label={contentType === 'visual_schedule' ? 'create visual schedule' : 'create story'}
              sx={{ width: 40, height: 40, ml: 1, bgcolor: '#274D3B', '&:hover': { bgcolor: '#1F3D30' } }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" fill="#3A6752"/><path d="M12 5V19" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/><path d="M7 10L12 5L17 10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </IconButton>
          </Box>
        </Box>
        {storyPages.length > 0 && (
          <Button variant="outlined" color="inherit" onClick={() => setPreviewOpen(true)} sx={{ mt: 2 }}>
            {generatedContentType === 'visual_schedule' ? (t?.sb_visual_schedule_preview || 'Preview Visual Schedule') : (t?.sb_social_story_preview || 'Preview Text Story')}
          </Button>
        )}
      </Box>

      <Dialog open={presetOpen} onClose={() => setPresetOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ position: 'relative', pt: 5 }}>
          <IconButton onClick={() => setPresetOpen(false)} sx={{ position: 'absolute', top: 8, right: 8 }}><CloseIcon /></IconButton>
          <SocialSkillPage
            t={t} skillCategory={skillCategory} setSkillCategory={setSkillCategory}
            socialSkill={socialSkill} setSocialSkill={setSocialSkill}
            socialSkillsData={activeCategoryData}
            categoryTitle={contentType === 'visual_schedule' ? (t?.sb_schedule_category || 'Schedule Category') : (t?.sb_skill_category || 'Social Skill Category')}
            getCategoryName={contentType === 'visual_schedule' ? getScheduleCategoryDisplayName : getLocalizedCategoryDisplayName}
            getSkillName={contentType === 'visual_schedule' ? getScheduleSkillDisplayName : undefined}
            selectionType={contentType === 'visual_schedule' ? 'schedule' : 'social-skill'}
            onPresetSelect={(preset) => { setSelectedPreset(preset); setPresetOpen(false); }}
            setManualSocialSkillInput={setPrompt} manualInputRef={inputRef}
            setSidebarContent={() => setPresetOpen(false)} setCollapsed={() => {}}
            setManualSocialSkill={() => {}} currentLang={currentLang}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={generating} maxWidth="xs" fullWidth sx={{ zIndex: 2500 }}>
        <DialogContent sx={{ py: 4 }}><LinearProgress sx={{ mb: 2 }} /><Typography variant="h6" align="center">{t?.social_story_generating_lbl || 'Generating story...'}</Typography></DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth sx={{ zIndex: 2200 }}>
        <DialogContent sx={{ position: 'relative', p: { xs: 0, sm: 3 }, mt: { xs: '56px', sm: 0 } }}>
          <TextStoryPage
            storyTitle={storyTitle} storyPages={storyPages} storyLanguage={roleStoryLanguage}
            voice={voice} imageStyle={visualStyle} onGenerateMedia={generateMedia}
            mediaUrl={mediaUrl} media={media} mediaLoading={mediaLoading} t={t}
            userPlan={userPlan} contentType={generatedContentType}
            disableGenerateMedia={!!mediaUrl && !mediaLoading && !/\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl)}
            renderMediaProgressBar={previewProgress} onClose={() => setPreviewOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
